import { useOutletContext, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { BlogReaderOutletContext } from "./BlogReaderLayout";
import { useBlog } from "../hooks/useBlog";
import VideoEmbed from "../../../components/common/VideoEmbed";
import VideoPlaylist from "../../../components/blog/VideoPlaylist";
import { Icon } from "../../../components/common/Icon";
import { FINISHED_PERCENT, getSeriesEntries, getEntry } from "../../../services/readingProgress";
import { useInternalLinkRouting } from "../../../hooks/useInternalLinkRouting";

const DIFFICULTY_LEVELS = ['beginner', 'intermediate', 'advanced'] as const;
const READ_MINS_PER_LESSON = 8;

export default function BlogOverviewPage() {
  const { blog, setPostSections } = useOutletContext<BlogReaderOutletContext>();
  const handleContentClick = useInternalLinkRouting();

  // Separate, draft-aware fetch purely for the roadmap + lesson count — the
  // outlet-context `blog` (published-only) keeps driving the sidebar TOC and
  // prev/next nav, which must never link to locked content.
  const { blog: roadmapBlog } = useBlog(blog.slug, { includeDraftMeta: true });

  // No right-sidebar sections on the overview page
  useEffect(() => {
    setPostSections([]);
  }, [setPostSections]);

  const [progress, setProgress] = useState<Record<string, number>>({});
  useEffect(() => {
    setProgress(getSeriesEntries(blog.slug));
  }, [blog.slug]);

  const posts = roadmapBlog?.posts ?? blog.posts;
  const postCount = posts.length;
  const totalReadMins = postCount * READ_MINS_PER_LESSON;
  const foundDifficulty = DIFFICULTY_LEVELS.find(d => blog.tags.includes(d));
  const difficulty = foundDifficulty
    ? foundDifficulty.charAt(0).toUpperCase() + foundDifficulty.slice(1)
    : 'Beginner';

  const firstPublishedPost = posts.find(p => p.status !== 'draft');
  const finishedCount = posts.filter(p => (progress[p.slug] ?? 0) >= FINISHED_PERCENT).length;

  // Resume beats "start" once this browser has read something in the series.
  const nextUnfinished = posts.find(
    p => p.status !== 'draft' && (progress[p.slug] ?? 0) < FINISHED_PERCENT
  );
  const ctaPost = finishedCount > 0 ? (nextUnfinished ?? firstPublishedPost) : firstPublishedPost;
  const ctaLabel = finishedCount > 0 ? 'Continue where you left off' : 'Start learning';

  // "Continue" should land where the reader stopped inside that chapter — but
  // the chapter it points at is often one they have never opened, and starting
  // those part-way down would be wrong. So only carry the position when this
  // browser actually has progress in that specific chapter.
  const ctaEntry = ctaPost ? getEntry(blog.slug, ctaPost.slug) : null;
  const ctaResumes = Boolean(ctaEntry?.percent);
  const ctaHref = ctaPost
    ? `/blog/${blog.slug}/${ctaPost.slug}${ctaEntry?.anchor ? `#${ctaEntry.anchor}` : ''}`
    : '';

  const backToSeriesHref = blog.course ? `/series/${blog.course.slug}` : '/blogs';
  const backToSeriesLabel = blog.course ? 'Back to series' : 'Back to all series';

  const isMultiPost = blog.posts.length > 1;
  const hasChapterVideos = blog.posts.some((p) => p.videoEmbedUrl);
  // A player is itself a two-pane component — it needs the full column width.
  const showsPlayer = (isMultiPost && hasChapterVideos) || Boolean(blog.videoEmbedUrl);

  const intro = (
    <header className="flex flex-col gap-5 max-w-3xl">
      <h1 className="font-display text-3xl sm:text-[2.75rem] font-extrabold leading-[1.08] tracking-[-0.03em] text-foreground text-pretty">
        {blog.title}
      </h1>

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex h-8 items-center rounded-full bg-surface-container px-3.5 text-[13px] font-semibold text-muted-foreground">
          {postCount} {postCount === 1 ? 'chapter' : 'chapters'}
        </span>
        <span className="inline-flex h-8 items-center gap-1.5 rounded-full bg-surface-container px-3.5 text-[13px] font-semibold text-muted-foreground">
          <Icon name="clock" size={14} />~{totalReadMins} min
        </span>
        <span className="inline-flex h-8 items-center rounded-full bg-surface-container px-3.5 text-[13px] font-semibold text-muted-foreground">
          {difficulty}
        </span>
        {finishedCount > 0 && (
          <span className="inline-flex h-8 items-center gap-1.5 rounded-full bg-secondary-container px-3.5 text-[13px] font-semibold text-on-secondary-container">
            <Icon name="check-circle" size={14} />
            {finishedCount} of {postCount} read
          </span>
        )}
      </div>

      <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground text-pretty">
        {blog.description}
      </p>

      <div className="flex flex-wrap items-center gap-4 pt-1">
        {ctaPost && (
          <Link
            to={ctaHref}
            state={ctaResumes ? { resume: true } : undefined}
            className="inline-flex h-12 items-center gap-2 rounded-xl bg-primary px-6 font-semibold text-primary-foreground transition-colors hover:bg-primary-container"
          >
            {ctaLabel}
            <Icon name="arrow-right" size={18} />
          </Link>
        )}
        {blog.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {blog.tags.slice(0, 4).map((tag) => (
              <span
                key={tag}
                className="inline-flex h-8 items-center rounded-full bg-primary-fixed px-3 text-[12px] font-bold uppercase tracking-wider text-primary"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </header>
  );

  return (
    <div className="flex-1">
      <Link
        to={backToSeriesHref}
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-foreground"
      >
        <Icon name="arrow-left" size={16} />
        {backToSeriesLabel}
      </Link>

      {showsPlayer ? (
        /* With a player, the media takes the full column and the intro sits above it. */
        <div className="flex flex-col gap-9">
          {intro}
          {isMultiPost && hasChapterVideos ? (
            <VideoPlaylist posts={blog.posts} blogSlug={blog.slug} />
          ) : (
            <VideoEmbed url={blog.videoEmbedUrl!} title={`${blog.title} — Intro`} />
          )}
        </div>
      ) : blog.coverImage ? (
        /* A cover can share the row with the intro. */
        <div className="grid items-start gap-10 md:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
          {intro}
          <img
            src={blog.coverImage}
            alt=""
            className="aspect-video w-full rounded-2xl border border-border object-cover"
          />
        </div>
      ) : (
        /* No media at all — an empty placeholder box just reads as broken. */
        intro
      )}

      {/* ── Chapter overview (optional long-form content) ───────────────────
          Not a lesson — the "what this chapter covers" write-up lives here,
          on the overview page itself, never as an entry in the roadmap.
          Constrained to the reading measure even though the page is wider. */}
      {blog.htmlContent && (
        <div
          className="prose mt-10 max-w-[46rem] border-t border-border pt-10"
          onClick={handleContentClick}
          dangerouslySetInnerHTML={{ __html: blog.htmlContent }}
        />
      )}

      {/* ── Chapters ─────────────────────────────────────────────────────── */}
      <div className="mt-12">
        <div className="mb-5 flex items-baseline justify-between gap-4">
          <h2 className="font-headline text-2xl font-bold tracking-tight text-foreground">
            Chapters
          </h2>
          {finishedCount > 0 && (
            <span className="text-sm font-semibold text-muted-foreground">
              {finishedCount} of {postCount} read
            </span>
          )}
        </div>

        <ol className="flex flex-col">
          {posts.map((post, idx) => {
            const isLocked = post.status === 'draft';
            const isFinished = (progress[post.slug] ?? 0) >= FINISHED_PERCENT;
            const number = String(post.order ?? idx + 1).padStart(2, '0');

            const marker = isLocked ? (
              <Icon name="lock" size={16} className="text-muted-foreground" aria-hidden />
            ) : isFinished ? (
              <Icon name="check-circle" size={18} className="text-secondary" aria-hidden />
            ) : (
              <span className="font-code text-xs tracking-widest text-muted-foreground">
                {number}
              </span>
            );

            const inner = (
              <>
                <span className="grid w-8 shrink-0 place-items-center">{marker}</span>
                <span className="min-w-0 flex-1 truncate font-semibold text-foreground">
                  {post.title}
                </span>
                {isLocked ? (
                  <span className="shrink-0 text-xs font-semibold text-muted-foreground">
                    Coming soon
                  </span>
                ) : (
                  <span className="flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
                    ~{post.readTime ?? 1} min
                    <Icon name="chevron-right" size={16} aria-hidden />
                  </span>
                )}
              </>
            );

            return (
              <li key={post.id}>
                {isLocked ? (
                  <div className="flex cursor-not-allowed items-center gap-4 border-b border-border px-3 py-4 opacity-55 last:border-0">
                    {inner}
                  </div>
                ) : (
                  <Link
                    to={`/blog/${blog.slug}/${post.slug}`}
                    className="flex items-center gap-4 rounded-xl border-b border-border px-3 py-4 transition-colors last:border-0 hover:bg-surface-container"
                  >
                    {inner}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
